import { cast, Instance, types } from 'mobx-state-tree';
import Common from '@store/common';
import {
  MAX_ALLOWED_CARD_PAYMENT,
  MAX_ALLOWED_CASH_PAYMENT,
  PaymentLinkRedirectParams,
  RedirectAutoOptions,
} from '@constants/paymentLink';
import PAYMENT_CONFIG from '@configs/payment';
import getUrlParam from '@utils/getUrlParam';

export enum Locales {
  mx = 'mx',
  us = 'us',
  ca = 'ca',
}
export const LocaleTypes = types.enumeration<Locales>(Object.values(Locales));

export enum Currencies {
  mxn = 'MXN',
}
export const CurrencyTypes = types.enumeration<Currencies>(Object.values(Currencies));

export enum PaymentMethod {
  card = 'CARD',
  cash = 'CASH',
  spei = 'SPEI',
  directDebit = 'DIRECT_DEBIT',
}

export const PaymentMethodMap = {
  [PaymentMethod.card]: 'CARD_PAYMENT',
  [PaymentMethod.cash]: 'CASH_PAYMENT',
  [PaymentMethod.spei]: 'SPEI_PAYMENT',
  [PaymentMethod.directDebit]: 'DIRECT_DEBIT',
};

export const PaymentMethodTypes = types.enumeration<PaymentMethod>(Object.values(PaymentMethod));

const CheckoutSuccessFlags = types
  .model({
    cardPayment: types.optional(types.boolean, false),
    cashPayment: types.optional(types.boolean, false),
    directDebitPayment: types.optional(types.boolean, false),
    subscription: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    set<K extends keyof Instance<typeof self>, T extends Instance<typeof self>>(
      key: K,
      value: T[K],
    ) {
      self[key] = cast(value);
    },
  }));

const CheckoutStore = types
  .compose(
    Common,
    types.model({
      locale: types.optional(LocaleTypes, Locales.mx),
      paymentMethod: types.optional(PaymentMethodTypes, PaymentMethod.card),
      success: types.optional(CheckoutSuccessFlags, {}),
    }),
  )
  .views((self) => ({
    get isSpei() {
      return self.paymentMethod === PaymentMethod.spei;
    },
    get isCardAvailable() {
      return PAYMENT_CONFIG.paymentMethods.card.locales.includes(self.locale);
    },
    get isCashAvailable() {
      return PAYMENT_CONFIG.paymentMethods.cash.locales.includes(self.locale);
    },
    get isSpeiAvailable() {
      return PAYMENT_CONFIG.paymentMethods.spei.locales.includes(self.locale);
    },
    get isDirectDebitAvailable() {
      return PAYMENT_CONFIG.paymentMethods.directDebit.locales.includes(self.locale);
    },
    get isAmountAllowedToCardPayment() {
      return +self.globalStore.links.paymentLink.total <= MAX_ALLOWED_CARD_PAYMENT;
    },
    get isAmountAllowedToCashPayment() {
      return +self.globalStore.links.paymentLink.total <= MAX_ALLOWED_CASH_PAYMENT;
    },
    get isAllowedToPayWithCard() {
      return (
        this.isAmountAllowedToCardPayment &&
        self.globalStore.links.paymentConfig.paymentMethods.card.enabled
      );
    },
    get isAllowedToPayWithSpei() {
      return !!self.globalStore.links.paymentConfig.paymentMethods.spei.enabled;
    },
    get isAllowedToPayWithCash() {
      return (
        this.isAmountAllowedToCashPayment &&
        self.globalStore.links.paymentConfig.paymentMethods.cash.enabled
      );
    },
    get isAllowedToPayWithDirectDebit() {
      return !!self.globalStore.links.paymentConfig.paymentMethods.directDebit.enabled;
    },
    get defaultPaymentMethod() {
      if (!this.isAmountAllowedToCardPayment) {
        return PaymentMethod.spei;
      }
      return PaymentMethod.card;
    },
    get isZipCodeRequired() {
      return self.locale !== Locales.mx;
    },
    get isEveryMethodDisabled() {
      if (self.globalStore.links.paymentLink.isSubscription) {
        return !this.isAllowedToPayWithCard && !this.isAllowedToPayWithDirectDebit;
      }

      return (
        !this.isAllowedToPayWithCard && !this.isAllowedToPayWithCash && !this.isAllowedToPayWithSpei
      );
    },
    get hasRedirectionUrl() {
      return !!getUrlParam(PaymentLinkRedirectParams.REDIRECT_URL);
    },
    get shouldAutoRedirect() {
      return (
        this.hasRedirectionUrl &&
        getUrlParam(PaymentLinkRedirectParams.AUTO_REDIRECT, RedirectAutoOptions.ENABLED) ===
          RedirectAutoOptions.ENABLED
      );
    },
  }))
  .actions((self) => ({
    set<K extends keyof Instance<typeof self>, T extends Instance<typeof self>>(
      key: K,
      value: T[K],
    ) {
      self[key] = cast(value);
    },
    setSubscriptionSuccess() {
      self.success.subscription = true;
    },
    setPaymentMethod(paymentMethod: PaymentMethod) {
      self.paymentMethod = cast(paymentMethod);
    },
    setCardPaymentSuccess() {
      self.success.cardPayment = true;
    },
    setCashPaymentSuccess() {
      self.success.cashPayment = true;
    },
    setDirectDebitPaymentSuccess() {
      self.success.directDebitPayment = true;
    },
    resetSuccessStatus() {
      self.success.cardPayment = false;
      self.success.cashPayment = false;
      self.success.directDebitPayment = false;
    },
  }));

export type ICheckoutStore = Instance<typeof CheckoutStore>;
export default CheckoutStore;
